import { folderTrail, UNASSIGNED_FOLDER_NAME } from '@src/utility/folderTrail';
import { checkWordsInString } from '@src/utility/general';

import { getVisibleClips } from './StoryManagerFolderView/clipDisplay';
import { getDescription } from './utils';

import type {
  DashboardStory,
  Folder,
  FolderStructure,
  OtherVideo,
} from '@src/types.ts/story';

/**
 * Result derivation for the Story Manager search dropdown. Pure functions so
 * the grouping/ordering rules are unit-testable in isolation (same idiom as
 * `StoryManagerFolderView/folderAggregates.ts`).
 */

export type ClipResult = { clip: OtherVideo; story: DashboardStory };

/** Which field the query hit. A title hit is self-evident; the others are not. */
export type StoryMatchField =
  | 'title'
  | 'storyteller'
  | 'speaker'
  | 'description';

/**
 * `folderPath` is where the story lives, as the full trail ("Marketing / Q3"),
 * so a result row says which (sub)folder it came from — the same thing the
 * Clips group does with its parent story.
 *
 * `matchLabel` is what made the row match when the title didn't: the
 * storyteller's or speaker's name, or an excerpt of the description. Without it
 * such a hit is invisible — the row shows a title that contains none of the
 * query, and nothing explains why it is in the list.
 */
export type StoryResult = {
  story: DashboardStory;
  folderPath: string;
  matchedOn: StoryMatchField;
  matchLabel?: string;
};

export type SearchDropdownResult =
  | { type: 'folder'; folder: Folder }
  | { type: 'story'; story: DashboardStory }
  | { type: 'clip'; clip: OtherVideo; story: DashboardStory };

export type SearchDropdownResults = {
  folderResults: Folder[];
  storyResults: StoryResult[];
  clipResults: ClipResult[];
  /**
   * All results in display order — Folders, then Stories, then Clips. This is
   * the single source of truth for BOTH render order and keyboard order, so
   * arrow-key navigation always moves through what the user sees.
   */
  flat: SearchDropdownResult[];
};

const EMPTY_RESULTS: SearchDropdownResults = {
  folderResults: [],
  storyResults: [],
  clipResults: [],
  flat: [],
};

/**
 * "Marketing / Q3", using the breadcrumb's `/` separator. An unfiled story, or
 * one mapped to a since-deleted folder, reads as Unassigned.
 */
const folderPathFor = (
  foldersById: Map<string, Folder>,
  folderId: string | undefined,
): string => {
  const trail = folderId ? folderTrail(foldersById, folderId).trail : [];
  return trail.length
    ? trail.map(f => f.name).join(' / ')
    : UNASSIGNED_FOLDER_NAME;
};

// Characters kept either side of the hit in a description excerpt. The subtitle
// truncates with an ellipsis anyway; this only keeps the useful part centred.
const SNIPPET_RADIUS = 40;

/**
 * Excerpt of `text` around the hit, so a description hit shows the words that
 * matched rather than the description's opening. Anchors on the whole phrase,
 * else the longest query word — a short word ("the") would anchor anywhere.
 * Every query word is present by the time this runs (`checkWordsInString`
 * requires it), so the anchor search only fails on an empty query, which never
 * reaches here.
 */
const buildMatchSnippet = (text: string, query: string): string => {
  const lower = text.toLowerCase();
  const words: string[] = query.toLowerCase().match(/\w+/g) ?? [];
  let at = words.length ? lower.indexOf(words.join(' ')) : -1;
  if (at === -1 && words.length) {
    const longest = words.reduce((a, b) => (b.length > a.length ? b : a));
    at = lower.indexOf(longest);
  }
  if (at === -1) return text.slice(0, SNIPPET_RADIUS * 2).trim();
  const start = Math.max(0, at - SNIPPET_RADIUS);
  const end = Math.min(text.length, at + SNIPPET_RADIUS);
  const lead = start > 0 ? '…' : '';
  const tail = end < text.length ? '…' : '';
  return `${lead}${text.slice(start, end).trim()}${tail}`;
};

/**
 * A story result means the STORY itself matched (title / storyteller / speaker
 * / description). Stories the page filter kept only because a clip matched
 * belong in the Clips group, not here. Returns null when nothing matched.
 *
 * Fields are tried in display priority, so the label names the most salient hit
 * and membership is identical to testing them with `||`.
 *
 * `title` / `storyTeller.name` are typed as always-present, but a story that
 * just came back from a CMA write carries `storyTeller` as the raw record ID
 * string (see `StoryRepository.itemToStory`), so the name is undefined until
 * the GraphQL list refreshes. Default both rather than throw mid-search.
 */
const matchStory = (
  story: DashboardStory,
  query: string,
): Pick<StoryResult, 'matchedOn' | 'matchLabel'> | null => {
  if (checkWordsInString(story.title ?? '', query)) {
    // The title is already the row's headline — a label would just repeat it.
    return { matchedOn: 'title' };
  }
  const storyteller = story.storyTeller?.name ?? '';
  if (checkWordsInString(storyteller, query)) {
    return { matchedOn: 'storyteller', matchLabel: storyteller };
  }
  // Speakers are matched on name only, never on `title` (their role): a search
  // for "CEO" should not return every story that happens to feature one. The
  // first match names the row — listing all of them would bury the folder.
  const speaker = (story.speakers ?? []).find(s =>
    checkWordsInString(s.name ?? '', query),
  );
  if (speaker) {
    return { matchedOn: 'speaker', matchLabel: speaker.name };
  }
  const description = getDescription(story);
  if (checkWordsInString(description, query)) {
    return {
      matchedOn: 'description',
      matchLabel: buildMatchSnippet(description, query),
    };
  }
  return null;
};

/**
 * @param structure the current showcase's folder structure — its folders are
 *   matched against the query, its `storyFolderMap` labels each story result
 * @param stories the page-filtered story list (the Dashboard search memo) —
 *   already narrowed to stories that matched the query directly or via clips
 * @param query the trimmed, debounced search query
 */
export const buildSearchDropdownResults = (
  structure: FolderStructure,
  stories: DashboardStory[],
  query: string,
): SearchDropdownResults => {
  if (!query) return EMPTY_RESULTS;

  const folderResults = structure.folders.filter(f =>
    checkWordsInString(f.name, query),
  );

  const foldersById = new Map(structure.folders.map(f => [f.id, f]));
  const storyResults: StoryResult[] = [];
  for (const story of stories) {
    const match = matchStory(story, query);
    if (!match) continue;
    storyResults.push({
      story,
      folderPath: folderPathFor(
        foldersById,
        structure.storyFolderMap[story.id],
      ),
      ...match,
    });
  }

  // Clips: only viewable ones (not hidden, backed by a video) whose title
  // matches. Story-level matches keep all their clips in the page memo, so
  // re-applying the title predicate here is what narrows them.
  const clipResults: ClipResult[] = [];
  for (const story of stories) {
    for (const clip of getVisibleClips(story)) {
      if (checkWordsInString(clip.title ?? '', query)) {
        clipResults.push({ clip, story });
      }
    }
  }

  return {
    folderResults,
    storyResults,
    clipResults,
    flat: [
      ...folderResults.map(
        (folder): SearchDropdownResult => ({ type: 'folder', folder }),
      ),
      ...storyResults.map(
        ({ story }): SearchDropdownResult => ({ type: 'story', story }),
      ),
      ...clipResults.map(
        ({ clip, story }): SearchDropdownResult => ({
          type: 'clip',
          clip,
          story,
        }),
      ),
    ],
  };
};
